import React, { useRef } from "react";
import { Button } from "reactstrap";
import Swal from "sweetalert2";

// Limites por subida, para que nadie mande por error una carpeta enorme
export const MAX_ARCHIVOS = 50;
export const MAX_BYTES = 100 * 1024 * 1024;

// Archivos de sistema que vienen dentro de las carpetas y no sirven
const BASURA = /(^|\/)(\.ds_store|thumbs\.db|desktop\.ini)$|(^|\/)\.[^/]*$/i;

// Nombre visible: si viene de una carpeta conserva su ruta ("Docs/BL.pdf")
export function nombreDe(archivo) {
  return archivo.webkitRelativePath || archivo.name;
}

function tamano(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Selector de archivos o de una carpeta completa.
 *   archivos   lista actual (File[])
 *   onChange   recibe la lista nueva
 *   unoSolo    solo permite un archivo (sin boton de carpeta)
 */
function SelectorArchivos({ archivos, onChange, unoSolo, deshabilitado }) {
  const refArchivos = useRef(null);
  const refCarpeta = useRef(null);
  const lista = archivos || [];

  function agrega(seleccion) {
    const nuevos = Array.from(seleccion || []).filter(
      (f) => f.size > 0 && !BASURA.test(nombreDe(f))
    );
    if (!nuevos.length) {
      Swal.fire({ icon: "info", title: "No hay archivos para subir en lo que elegiste" });
      return;
    }
    const base = unoSolo ? [] : lista;
    const ya = new Set(base.map(nombreDe));
    const juntos = unoSolo
      ? nuevos.slice(0, 1)
      : [...base, ...nuevos.filter((f) => !ya.has(nombreDe(f)))];
    const total = juntos.reduce((t, f) => t + f.size, 0);
    if (juntos.length > MAX_ARCHIVOS) {
      Swal.fire({
        icon: "warning",
        title: `Maximo ${MAX_ARCHIVOS} archivos por subida`,
        text: `Elegiste ${juntos.length}. Subelos en varias partes o comprime la carpeta en un ZIP.`,
      });
      return;
    }
    if (total > MAX_BYTES) {
      Swal.fire({
        icon: "warning",
        title: `Maximo ${tamano(MAX_BYTES)} por subida`,
        text: `Lo que elegiste pesa ${tamano(total)}. Subelo en varias partes.`,
      });
      return;
    }
    onChange(juntos);
  }

  function quita(nombre) {
    onChange(lista.filter((f) => nombreDe(f) !== nombre));
  }

  const total = lista.reduce((t, f) => t + f.size, 0);

  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <Button
          size="sm"
          color="secondary"
          disabled={deshabilitado}
          title={unoSolo ? "Elegir un archivo" : "Elegir uno o varios archivos"}
          onClick={() => refArchivos.current && refArchivos.current.click()}
        >
          <i class="fas fa-file-upload"></i> {unoSolo ? "Elegir archivo" : "Elegir archivos"}
        </Button>
        {!unoSolo ? (
          <Button
            size="sm"
            color="secondary"
            disabled={deshabilitado}
            title="Elegir una carpeta: se suben todos los archivos que tenga, incluidas subcarpetas"
            onClick={() => refCarpeta.current && refCarpeta.current.click()}
          >
            <i class="fas fa-folder-open"></i> Subir carpeta
          </Button>
        ) : null}
      </div>
      <input
        ref={refArchivos}
        type="file"
        multiple={!unoSolo}
        style={{ display: "none" }}
        onChange={(e) => {
          agrega(e.target.files);
          e.target.value = "";
        }}
      />
      {!unoSolo ? (
        <input
          ref={refCarpeta}
          type="file"
          webkitdirectory=""
          directory=""
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            agrega(e.target.files);
            e.target.value = "";
          }}
        />
      ) : null}

      {lista.length ? (
        <div
          style={{
            marginTop: 8,
            maxHeight: 180,
            overflowY: "auto",
            border: "1px solid #e2e8f0",
            borderRadius: 6,
            padding: "4px 8px",
          }}
        >
          {lista.map((f) => (
            <div
              key={nombreDe(f)}
              style={{ display: "flex", alignItems: "center", fontSize: 12, padding: "2px 0" }}
            >
              <i class="far fa-file" style={{ color: "#94a3b8", marginRight: 6 }}></i>
              <span style={{ flex: 1, wordBreak: "break-all" }}>{nombreDe(f)}</span>
              <span style={{ color: "#94a3b8", margin: "0 8px", whiteSpace: "nowrap" }}>
                {tamano(f.size)}
              </span>
              <button
                type="button"
                title="Quitar de la lista"
                onClick={() => quita(nombreDe(f))}
                disabled={deshabilitado}
                style={{ border: 0, background: "none", color: "#b91c1c", cursor: "pointer" }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {lista.length > 1 ? (
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
          {lista.length} archivos · {tamano(total)}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Sube una lista de archivos uno por uno, mostrando el avance.
 * subeUno(archivo, nombre) debe regresar la promesa de la peticion.
 * Si uno falla los demas se siguen subiendo; al final dice cuales fallaron.
 * Regresa { subidos, fallidos, ultimaRespuesta }.
 */
export async function subirEnSerie(archivos, subeUno, titulo) {
  const fallidos = [];
  let subidos = 0;
  let ultimaRespuesta = null;
  Swal.fire({
    title: titulo || "Subiendo archivos",
    html: '<div id="sa-avance" style="font-size:14px">Preparando...</div>',
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
  });
  const avance = (texto) => {
    const contenedor = (Swal.getHtmlContainer && Swal.getHtmlContainer()) || Swal.getContent();
    const el = contenedor && contenedor.querySelector("#sa-avance");
    if (el) el.textContent = texto;
  };
  for (let i = 0; i < archivos.length; i++) {
    const archivo = archivos[i];
    const nombre = nombreDe(archivo);
    avance(`${i + 1} de ${archivos.length}: ${nombre}`);
    try {
      ultimaRespuesta = await subeUno(archivo, nombre);
      subidos++;
    } catch (error) {
      console.log(error);
      fallidos.push({
        nombre,
        motivo: error.response?.data?.message || error.message || "Error",
      });
    }
  }
  Swal.close();
  return { subidos, fallidos, ultimaRespuesta };
}

/** Mensaje final de una subida en serie. */
export function avisaResultado({ subidos, fallidos }, textoOk) {
  if (!fallidos.length) {
    return Swal.fire({
      icon: "success",
      title: subidos === 1 ? "Archivo guardado" : `${subidos} archivos guardados`,
      text: textoOk || "",
    });
  }
  return Swal.fire({
    icon: subidos ? "warning" : "error",
    title: subidos
      ? `Se subieron ${subidos}, fallaron ${fallidos.length}`
      : "No se pudo subir ningun archivo",
    html: `<div style="text-align:left;font-size:13px">${fallidos
      .map((f) => `<div>• <b>${f.nombre.replace(/</g, "&lt;")}</b>: ${String(f.motivo).replace(/</g, "&lt;")}</div>`)
      .join("")}</div>`,
  });
}

export default SelectorArchivos;
